/**
 * SB5-A — Accept-invitation public page.
 *
 * The user clicks the link in the invitation email (sent by Zitadel
 * via mailhog in local dev), lands here, and sets their initial
 * password. The page is NO-AUTH (no RequireAuth gate); the URL carries:
 *
 *   /invite/:code?userId=<zitadel-user-id>&orgId=<zitadel-org-id>
 *
 * Submit posts `{ password }` to
 *   POST /api/auth/v1/invitations/:code/accept?userId=<zitadel-user-id>
 *
 * On success: navigate to `/login?invitation_accepted=1` with a flash
 * (so the user sees "Your account is ready — sign in" on the login
 * page). On failure: surface the BE error inline via `<Alert>`.
 *
 * Visual style mirrors the existing `LoginPage` (Apple-clean rhythm
 * from `.knowledge/CI_custom/`), reusing `LoginCard` from
 * `@aeriosiq/auth-client`.
 *
 * Locked decisions (per parent .feedback brief):
 *   Q10 — Zitadel says user-exists → 409. We surface the BE's
 *         `code: 'zitadel_user_exists'` message inline.
 *   Q11 — No custom welcomeMessage in the email body. Nothing to
 *         render here from that field.
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LoginCard, getAuthBaseUrl } from "@aeriosiq/auth-client";
import { radius, typography } from "@aeriosiq/ui";

export const PASSWORD_MIN_LENGTH = 12;

export interface ValidatePasswordResult {
  ok: boolean;
  reason?:
    | "password_required"
    | "password_too_short"
    | "passwords_do_not_match";
}

/**
 * Pure-function password validator extracted for vitest coverage —
 * mirrors the rules the BE enforces (≥ 12 chars; passwords must match).
 */
export function validatePassword(
  password: string,
  confirm: string,
): ValidatePasswordResult {
  if (!password || password.length === 0) {
    return { ok: false, reason: "password_required" };
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return { ok: false, reason: "password_too_short" };
  }
  if (password !== confirm) {
    return { ok: false, reason: "passwords_do_not_match" };
  }
  return { ok: true };
}

export function AcceptInvitePage(): ReactElement {
  const params = useParams<{ code: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const code = params.code ?? "";
  const userId = search.get("userId") ?? "";
  // orgId is provided by Zitadel's URL-template expansion for future
  // multi-org disambiguation; the BE doesn't currently need it but we
  // accept + ignore it so links keep working.
  const orgId = search.get("orgId") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validation = useMemo(
    () => validatePassword(password, confirm),
    [password, confirm],
  );

  const showMismatch =
    confirm.length > 0 && password !== confirm && password.length > 0;

  async function submit(): Promise<void> {
    setError(null);
    const v = validatePassword(password, confirm);
    if (!v.ok) {
      if (v.reason === "password_too_short") {
        setError(
          `Password must be at least ${String(PASSWORD_MIN_LENGTH)} characters.`,
        );
      } else if (v.reason === "passwords_do_not_match") {
        setError("Passwords don't match.");
      } else {
        setError("Please enter a password.");
      }
      return;
    }
    if (!userId) {
      setError(
        "This invitation link is missing the user identifier. Please ask your admin to resend.",
      );
      return;
    }
    if (!code) {
      setError(
        "This invitation link is missing the invitation code. Please ask your admin to resend.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const base = getAuthBaseUrl();
      const url = `${base}/invitations/${encodeURIComponent(code)}/accept?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          // non-JSON
        }
        const bodyCode = (body as { code?: string } | null)?.code;
        if (res.status === 404) {
          setError(
            "This invitation has expired or has already been used. Ask your admin to resend.",
          );
        } else if (res.status === 400) {
          setError(
            "The password doesn't meet our requirements. Please try a stronger one.",
          );
        } else if (bodyCode === "zitadel_user_exists") {
          setError(
            "A user with this email already exists. Sign in instead, or ask your admin.",
          );
        } else {
          setError("Couldn't accept the invitation. Please try again.");
        }
        setSubmitting(false);
        return;
      }
      // Success — the BE returns 204. Drop the user on /login with a
      // flag the LoginPage can read to flash "your account is ready —
      // sign in" on its next mount. We can't fire the protected-area
      // ToastContext from this public route, so we use a query param
      // the LoginPage can read.
      void navigate("/login?invitation_accepted=1", { replace: true });
    } catch (err) {
      setSubmitting(false);
      setError("Couldn't reach the server. Try again in a moment.");
      console.error("acceptInvitation failed", err);
    }
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    void submit();
  }

  return (
    <LoginCard>
      <Stack spacing={2} alignItems="center">
        <Typography
          component="h1"
          textAlign="center"
          sx={{
            fontSize: {
              xs: typography.size["display-sm"],
              sm: typography.size["display-md"],
              md: typography.size["display-lg"],
            },
            fontWeight: typography.weight.semibold,
            letterSpacing: typography.tracking["display-lg"],
            lineHeight: 1.05,
          }}
        >
          Welcome to AeriosIQ
        </Typography>
        <Typography
          color="text.secondary"
          textAlign="center"
          sx={{
            fontSize: typography.size["title-md"],
            letterSpacing: typography.tracking["title-md"],
          }}
        >
          Set a password to finish setting up your account
        </Typography>
      </Stack>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            fullWidth
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            disabled={submitting}
            helperText={`At least ${String(PASSWORD_MIN_LENGTH)} characters.`}
            slotProps={{
              htmlInput: { "data-testid": "accept-invite-password" },
            }}
          />
          <TextField
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            fullWidth
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setError(null);
            }}
            disabled={submitting}
            error={showMismatch}
            helperText={showMismatch ? "Passwords don't match." : undefined}
            slotProps={{
              htmlInput: { "data-testid": "accept-invite-confirm" },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            color="primary"
            disabled={submitting || !validation.ok}
            aria-busy={submitting}
            sx={{
              minHeight: 44,
              borderRadius: `${String(radius.pill)}px`,
            }}
          >
            {submitting ? (
              <>
                <CircularProgress
                  size={20}
                  aria-hidden="true"
                  sx={{ mr: 1, color: "inherit" }}
                />
                Setting password&hellip;
              </>
            ) : (
              "Set password and continue"
            )}
          </Button>
          {error ? (
            <Alert role="alert" severity="error">
              {error}
            </Alert>
          ) : null}
        </Stack>
      </Box>
      {/* orgId is captured from the URL for future multi-org wiring;
          reference it so the linter doesn't strip the param. */}
      <Box sx={{ display: "none" }} data-org-id={orgId} />
    </LoginCard>
  );
}
